import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

export function FormattedTimestamp({
  timestamp,
  short = false,
  absolute = false,
  className,
  onClick,
  href
}: {
  timestamp: number
  short?: boolean
  absolute?: boolean
  className?: string
  onClick?: (e: React.MouseEvent) => void
  href?: string
}) {
  const time = dayjs(timestamp * 1000)
  const fullTimestamp = time.format('YYYY-MM-DD HH:mm:ss')

  if (href) {
    return (
      <a
        href={href}
        className={cn(className, 'cursor-pointer hover:underline')}
        title={fullTimestamp}
        onClick={onClick}
      >
        <FormattedTimestampContent timestamp={timestamp} short={short} absolute={absolute} />
      </a>
    )
  }

  return (
    <span
      className={cn(className, onClick && 'cursor-pointer hover:underline')}
      title={fullTimestamp}
      onClick={onClick}
    >
      <FormattedTimestampContent timestamp={timestamp} short={short} absolute={absolute} />
    </span>
  )
}

function FormattedTimestampContent({
  timestamp,
  short = false,
  absolute = false
}: {
  timestamp: number
  short?: boolean
  absolute?: boolean
}) {
  const { t } = useTranslation()
  const time = dayjs(timestamp * 1000)
  const now = dayjs()

  if (absolute) return time.format('YYYY-MM-DD HH:mm:ss')

  if (time.isAfter(now)) {
    return short
      ? t('{{timestamp, date}}', { timestamp: time.valueOf() })
      : t('Scheduled for {{timestamp, date}}', { timestamp: time.valueOf() })
  }

  const diffMonth = now.diff(time, 'month')
  if (diffMonth >= 2) {
    return t('{{timestamp, date}}', { timestamp: time.valueOf() })
  }
  if (diffMonth >= 1) {
    const diffWeek = now.diff(time, 'week')
    if (!short && diffWeek >= 2) {
      return t('almost {{n}} months ago', { n: diffMonth + 1 })
    }
    return short ? t('{{n}} mo', { n: diffMonth }) : t('{{n}} month ago', { n: diffMonth })
  }

  const diffDay = now.diff(time, 'day')
  if (diffDay >= 1) {
    const diffHour = now.diff(time, 'hour')
    if (!short && diffHour >= diffDay * 24 + 16) {
      return t('almost {{n}} days ago', { n: diffDay + 1 })
    }
    return short ? t('{{n}} d', { n: diffDay }) : t('{{n}} days ago', { n: diffDay })
  }

  const diffHour = now.diff(time, 'hour')
  if (diffHour >= 1) {
    const diffMinute = now.diff(time, 'minute')
    if (!short && diffMinute >= diffHour * 60 + 40) {
      return t('almost {{n}} hours ago', { n: diffHour + 1 })
    }
    return short ? t('{{n}} h', { n: diffHour }) : t('{{n}} hours ago', { n: diffHour })
  }

  const diffMinute = now.diff(time, 'minute')
  if (diffMinute >= 1) {
    const diffSecond = now.diff(time, 'second')
    if (!short && diffSecond >= diffMinute * 60 + 40) {
      return t('almost {{n}} minutes ago', { n: diffMinute + 1 })
    }
    return short ? t('{{n}} m', { n: diffMinute }) : t('{{n}} minutes ago', { n: diffMinute })
  }

  return t('just now')
}
